import type { AnalyticsObject, AnalyticsProperties } from '@core/analytics/types'
import { type CorridorProperties, mapCorridor } from '@core/analytics/objects/Corridor'
import { NA } from '@core/analytics/helpers/non-available'
import { type ComparisonSectionType, SECTIONS_MAP } from './ComparisonSection'
import { type Competitiveness, getCompetitiveness } from '../competitiveness'
import type { Tab, Filters } from '../../types'
import type { Corridor, PaymentType } from '@core/types/imt'
import type { GQLTransferComparison } from '../../generated/transfer-comparison'
import { SENDING_MAP } from './ComparisonResult'
import { providerHasNegativeExposure, providerHasPositiveExposure } from '../../helpers/spotlight'
import { totalCost } from '../../helpers/pricing'

export type ComparisonData = {
  loadingTime: { start: number; end: number }
  corridor: Corridor
  data: GQLTransferComparison['results']
  tabs: Tab[]
  filters: Filters
  comparisonPromosHidden?: boolean
}

export class Comparison implements AnalyticsObject {
  name: string
  properties: ComparisonProperties
  constructor(properties: ComparisonProperties) {
    this.name = 'IMT Comparison'
    this.properties = properties
  }

  static getInstance({
    loadingTime,
    corridor,
    data,
    tabs,
    filters,
    comparisonPromosHidden,
  }: ComparisonData) {
    const metadata = {
      corridor,
      midMarketRate: data.midMarket.rate,
      amountUSD: data.corridor.amountUSD,
    }
    return new Comparison({
      ...mapCorridor({ ...corridor, amountUsd: data.corridor.amountUSD }),
      comparison: {
        comparisonId: data.comparisonId,
        loadingTimeMillis: getLoadingTime(loadingTime),
        nbSections: tabs.length,
        nbResultsTotal: tabs.reduce((sum, tab) => sum + tab.items.length, 0),
        firstSection: firstSection(tabs),
        cheapestSection: cheapestSections(tabs),
        costDifferenceBankCash: costDifferenceBankCash(tabs),
        bankSectionCheapestProvider: tabCheapestProvider(tabs, 'bank'),
        bankSectionFirstProvider: tabFirstProvider(tabs, 'bank'),
        cashSectionCheapestProvider: tabCheapestProvider(tabs, 'cash'),
        cashSectionFirstProvider: tabFirstProvider(tabs, 'cash'),
        listProvidersAvailable: availableProviders(tabs),
        listProvidersVeryCheap: competitiveProviders(tabs, metadata, 'Very Cheap'),
        listProvidersCheap: competitiveProviders(tabs, metadata, 'Cheap'),
        listProvidersAverage: competitiveProviders(tabs, metadata, 'Average'),
        listProvidersExpensive: competitiveProviders(tabs, metadata, 'Expensive'),
        listProvidersVeryExpensive: competitiveProviders(tabs, metadata, 'Very Expensive'),
        listProvidersFilteredOut: filteredOutProviders(data, filters),
        activeFilters: {
          payin: filters.payin?.map((payin) => SENDING_MAP[payin]),
          usedProviders: filters.usedProviders,
        },
      },
      comparisonPromosHidden,
    })
  }
}

export interface ComparisonProperties extends AnalyticsProperties, CorridorProperties {
  comparison: {
    comparisonId: string
    loadingTimeMillis: number | undefined
    nbSections: number
    nbResultsTotal: number
    firstSection: ComparisonSectionType | undefined
    cheapestSection: string[]
    costDifferenceBankCash: number | typeof NA
    bankSectionCheapestProvider: string
    bankSectionFirstProvider: string
    cashSectionCheapestProvider: string
    cashSectionFirstProvider: string
    listProvidersAvailable: string[]
    listProvidersVeryCheap: string[]
    listProvidersCheap: string[]
    listProvidersAverage: string[]
    listProvidersExpensive: string[]
    listProvidersVeryExpensive: string[]
    listProvidersFilteredOut: string[]
    activeFilters: {
      payin?: string[]
      usedProviders?: string[]
    }
  }
  comparisonPromosHidden?: boolean
}

function getLoadingTime(time?: { start: number; end: number }): number | undefined {
  return time ? time.end - time.start : undefined
}

function firstSection(tabs: Tab[]) {
  return tabs.length ? SECTIONS_MAP[tabs[0].type] : undefined
}

function cheapestSections(tabs: Tab[]) {
  return tabs.filter((tab) => tab.label === 'best_deal').map((tab) => SECTIONS_MAP[tab.type])
}

export function costDifferenceBankCash(tabs: Tab[]) {
  const bank = tabs.find((tab) => tab.type === 'bank')
  const cash = tabs.find((tab) => tab.type === 'cash')
  if (!bank || !cash) {
    return NA
  }
  return bank.amount - cash.amount
}

function tabCheapestProvider(tabs: Tab[], tabType: PaymentType) {
  const tab = tabs.find((r) => r.type === tabType)
  return tab?.items.find((i) => i.bestDeal)?.psp.name ?? NA
}

function tabFirstProvider(tabs: Tab[], tabType: PaymentType) {
  const tab = tabs.find((r) => r.type === tabType)
  return tab?.items[0]?.psp.name ?? NA
}

function availableProviders(tabs: Tab[]) {
  const psps = tabs.flatMap((tab) => tab.items).map((r) => r.psp.name)
  return [...new Set(psps)]
}

export function competitiveProviders(
  tabs: Tab[],
  {
    corridor,
    midMarketRate,
    amountUSD,
  }: { corridor: Corridor; midMarketRate: number; amountUSD: number },
  level: Competitiveness,
) {
  const amount = corridor.amount || corridor.defaultAmount
  const psps = tabs
    .flatMap((tab) => tab.items)
    .filter((item) => {
      const quote = item.cheapestService.quote
      return getCompetitiveness(totalCost(amount, midMarketRate, quote), amountUSD) === level
    })
    .map((r) => r.psp.name)
  return [...new Set(psps)]
}

function filteredOutProviders(data: GQLTransferComparison['results'], filters: Filters) {
  const filteredOutProviders: string[] = filters.payin
    ? data.providerQuotes
        .filter((item) => item.quotes.filter((q) => filters.payin?.includes(q.payin)).length === 0)
        .map((pq) => pq.psp.name)
    : []
  const filteredQuotes = data.providerQuotes.filter(
    (pq) => !filteredOutProviders.includes(pq.psp.name),
  )
  if (filteredQuotes.some((pq) => providerHasPositiveExposure(pq.quotes, filters))) {
    filteredQuotes.forEach((pq) => {
      if (
        !filters.usedProviders?.includes(pq.psp.slug) &&
        providerHasNegativeExposure(pq.quotes, filters)
      ) {
        filteredOutProviders.push(pq.psp.name)
      }
    })
  }
  return filteredOutProviders
}
